<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import {useStore} from 'vuex'
import {useRouter, useRoute} from 'vue-router'
import {LogOut, User, FileSliders, LifeBuoy, Settings} from 'lucide-vue-next'
import { firebase } from '@/router'
import { API, HOST } from '@/utils/http'

import { Search } from '@/components/filters'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'

const router = useRouter()
const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const member = computed(() => store.getters['getMember'])
const orgs = computed(() => store.getters['org/getOrgs'])
const selectedOrgId = computed(() => store.getters['org/getSelected'])
const selectedOrg = computed(() => store.getters['org/getSelectedOrg'])

const currentOrg = computed(() => {
  return selectedOrg.value || null
})

const sortedOrgs = computed(() => {
  return [...orgs.value].sort((a, b) =>
    a.organization_name.localeCompare(b.organization_name)
  )
})

const initials = computed(() => {
  const name = member.value.display_name
  if (!name) {
    return '?'
  }
  const names = name.split(' ')
  const first = names[0].charAt(0)
  const last = names[names.length - 1].charAt(0)
  return first + last
})

const logOutUser = () => {
  store.dispatch('logout')
}

const isLoggedIn = computed(() => {
  return store.getters['getIsLoggedIn']
})

const handleOrgChange = (orgId) => {
  store.dispatch('org/setSelectedOrg', orgId)
}

const initSelectedOrg = () => {
  store.dispatch('org/initSelectedOrg')
}

const resetView = () => {
  const defaultOptions = {
    center: [-21.874, 42.76],
    zoom: 0
  }
  map.value.setCenter(defaultOptions.center)
  map.value.setZoom(defaultOptions.zoom)

  router.push('/')
}

const getZendeskSsoUrl = async () => {
  try {
    const firebaseToken = await firebase.auth.currentUser.getIdToken(true)

    const response = await API({
      method: 'POST',
      url: `${HOST}/zendesk-auth`,
      data: {
        firebase_token: firebaseToken
      }
    })

    return response.data?.url || ''
  } catch (error) {
    console.error('Error fetching Zendesk SSO URL:', error);
  }
}

const zendeskUrl = ref(null)

onMounted(async () => {
  if (isLoggedIn.value) {
    zendeskUrl.value = await getZendeskSsoUrl()
    initSelectedOrg()
  }
})

// Watch for orgs changes to initialize selected org
watch(() => orgs.value, (newOrgs) => {
  if (newOrgs.length > 0 && !selectedOrgId.value) {
    initSelectedOrg()
  }
})
</script>

<template>
  <header class="h-full bg-white flex items-center px-1">
    <div class="flex items-center flex-1">
      <div class="hidden md:flex md:mr-2" @click="resetView">
        <Avatar class="w-9 h-9 border border-gray-300 cursor-pointer hover:opacity-80 rounded-lg">
          <AvatarImage v-if="currentOrg && currentOrg.avatar_url" :src="currentOrg.avatar_url" :alt="currentOrg.organization_name" />
          <img v-else src="@/assets/img/main-logo.svg" alt="logo" style="height: 32px; min-width: 32px;" />
        </Avatar>
      </div>

      <div v-if="isLoggedIn && sortedOrgs.length > 1" class="mr-3">
        <div class="flex items-center">
          <Select :model-value="selectedOrgId" @update:model-value="handleOrgChange">
            <SelectTrigger class="border-0 p-0 w-auto">
              <SelectValue placeholder="Select an organization">
                <b>{{ selectedOrg?.organization_name }}</b>
              </SelectValue>
            </SelectTrigger>
            <SelectContent class="max-h-[300px]">
              <SelectGroup>
                <SelectItem
                  v-for="org in sortedOrgs"
                  :key="org.organization_id"
                  :value="org.organization_id.toString()"
                >
                  {{ org.organization_name }}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <router-link v-if="selectedOrgId" :to="{ name: 'OrgMembers', params: { org_id: selectedOrgId }}">
            <Button class="rounded-full w-7 h-7 p-0" variant="ghost" size="sm">
              <Settings class="w-4 h-4 text-gray-500" />
            </Button>
          </router-link>
        </div>
      </div>

      <div v-else-if="isLoggedIn && sortedOrgs.length === 1" class="mr-3">
        <div class="flex items-center">
          <b>{{ selectedOrg?.organization_name }}</b>
          <router-link v-if="selectedOrgId" :to="{ name: 'OrgMembers', params: { org_id: selectedOrgId }}">
            <Button class="rounded-full w-7 h-7 p-0 ml-2" variant="ghost" size="sm">
              <Settings class="w-4 h-4 text-gray-500" />
            </Button>
          </router-link>
        </div>
      </div>
    </div>

    <div v-if="isLoggedIn" class="flex-1 flex justify-center">
      <div class="hidden md:block">
        <Search />
      </div>
    </div>

    <div v-if="isLoggedIn" class="flex items-center justify-end flex-1">
      <DropdownMenu>
        <DropdownMenuTrigger class="flex items-center">
          <div class="flex flex-col items-end">
            <span class="mr-2 text-sm font-bold" style="line-height: 1;">{{member.display_name}}</span>
            <span v-if="member.email" class="mr-2 text-xs text-gray-400">{{member.email}}</span>
            <span v-else-if="!member.email && member.phone" class="mr-2 text-xs text-gray-400">{{member.phone}}</span>
          </div>
          <Avatar class="w-9 h-9">
            <AvatarImage :src="`${member.avatar_url}`" :alt="member.display_name" />
            <AvatarFallback>{{initials}}</AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-56">
          <DropdownMenuItem @click="router.push('/user')">
            <User class="mr-2 h-4 w-4" />
            <span>Profile</span>
          </DropdownMenuItem>
          <DropdownMenuItem v-if="member.is_super_user" @click="router.push('/admin')">
            <FileSliders class="mr-2 h-4 w-4" />
            <span>Admin Tools</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <a v-if="zendeskUrl" :href="zendeskUrl" target="_blank">
            <DropdownMenuItem>
              <LifeBuoy class="mr-2 h-4 w-4" />
              <span>Help Centre</span>
            </DropdownMenuItem>
          </a>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="logOutUser">
            <LogOut class="mr-2 h-4 w-4" />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </header>
</template>
